import { clamp } from '@radix-ui/number';
import * as SliderPrimitive from '@radix-ui/react-slider';
import * as React from 'react';
import { cn } from '#app/utils/misc.tsx';

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    highlightedRanges?: [number, number][];
    trackClassName?: string;
    rangeClassName?: string;
    highlightedRangeClassName?: string;
    thumbClassName?: string;
    activeRange: [number, number];
  }
>(
  (
    {
      className,
      highlightedRanges = [],
      highlightedRangeClassName,
      trackClassName,
      thumbClassName,
      rangeClassName,
      activeRange,
      ...props
    },
    ref,
  ) => (
    <SliderPrimitive.Root
      ref={ref}
      className={cn(
        'relative flex w-full touch-none select-none items-center',
        className,
      )}
      {...props}
    >
      <SliderPrimitive.Track
        className={cn(
          'relative h-2 w-full grow overflow-hidden rounded-full bg-accent',
          trackClassName,
        )}
      >
        <SliderPrimitive.Range
          className={cn('absolute h-full bg-primary', rangeClassName)}
        />

        {highlightedRanges.map((highlightedRange) => (
          <HighlightedRange
            key={highlightedRange.join('-')}
            startValue={highlightedRange[0]}
            endValue={highlightedRange[1]}
            min={props.min}
            max={props.max}
            className={highlightedRangeClassName}
          />
        ))}
        {activeRange.length > 0 && (
          <>
            <HighlightedRange
              min={props.min as number}
              max={props.max as number}
              startValue={props.min as number}
              endValue={activeRange[0]}
              className="opacity-70"
            />
            <HighlightedRange
              min={props.min as number}
              max={props.max as number}
              startValue={activeRange[1]}
              endValue={props.max as number}
              className="opacity-70"
            />
          </>
        )}
      </SliderPrimitive.Track>
      {props.defaultValue?.map((_, i) => (
        <SliderPrimitive.Thumb
          key={i}
          className={cn(
            'block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
            thumbClassName,
          )}
        />
      ))}
    </SliderPrimitive.Root>
  ),
);

function convertValueToPercentage(value: number, min: number, max: number) {
  const maxSteps = max - min;
  const percentPerStep = 100 / maxSteps;
  const percentage = percentPerStep * (value - min);
  return clamp(percentage, [0, 100]);
}

export const HighlightedRange = ({
  className,
  startValue,
  endValue,
  min = 0,
  max = 100,
  ...props
}: {
  startValue: number;
  endValue: number;
  min?: number;
  max?: number;
  className?: string;
}) => {
  const startPercentage = convertValueToPercentage(startValue, min, max);
  const endPercentage = convertValueToPercentage(endValue, min, max);
  return (
    <span
      {...props}
      className={cn('absolute h-full bg-secondary', className)}
      style={{
        left: `${startPercentage}%`,
        right: `${100 - endPercentage}%`,
      }}
    />
  );
};

Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
